/**
 * Test Mode Mock Data for Product Listing
 *
 * This file provides mock data for the product listing feature when running in test mode.
 * Used for E2E tests to bypass Firestore/API calls and provide consistent, predictable data.
 *
 * Enable test mode via:
 * - URL param: ?testMode=true
 * - localStorage: localStorage.setItem('testMode', 'true')
 * - Env var: VITE_TEST_MODE=true
 */

import type { Store, Product, ProductListingApiResponse } from '../types/ProductListing'

/**
 * Mock accessible stores (replaces Firestore account query)
 */
export const mockStores: Store[] = [
  {
    id: 'acc-shopee-001',
    name: 'Shopee - Official Store SG',
    marketplace: 'shopee',
    logo: 'https://via.placeholder.com/40x40?text=SG',
    url: 'https://shopee.sg/shop/123456'
  },
  {
    id: 'acc-shopee-002',
    name: 'Shopee - Official Store MY',
    marketplace: 'shopee',
    logo: 'https://via.placeholder.com/40x40?text=MY',
    url: 'https://shopee.com.my/shop/789012'
  },
  {
    id: 'acc-lazada-001',
    name: 'Lazada - Lazada Official Store',
    marketplace: 'lazada',
    logo: 'https://via.placeholder.com/40x40?text=LZ',
    url: 'https://lazada.sg/shop/sevendays'
  },
  {
    id: 'acc-tokopedia-001',
    name: 'Tokopedia - Tokopedia Store',
    marketplace: 'tokopedia',
    logo: 'https://via.placeholder.com/40x40?text=TP',
    url: 'https://tokopedia.com/sevendays-official'
  },
  {
    id: 'acc-tiktok-001',
    name: 'Tiktok - TikTok Shop',
    marketplace: 'tiktok',
    logo: 'https://via.placeholder.com/40x40?text=TT',
    url: 'https://tiktok.com/shop/sevendays'
  }
]

/**
 * Mock products for product listing
 */
export const mockProducts: Product[] = [
  {
    id: 'prod-001',
    sku: 'SKU-SHP-001',
    productListingId: 'pl-001',
    accountId: 'acc-shopee-001',
    marketplace: 'shopee',
    title: 'Classic Chronograph Watch - Silver',
    description: 'High-quality wireless headphones with noise cancellation',
    brandName: 'TechBrand',
    status: 'live',
    statusNote: '',
    isActive: true,
    category: [],
    price: {
      current: 19999,
      original: 24999,
      currency: 'USD'
    },
    inventoryAllocation: {
      'warehouse-main': 100,
      'warehouse-secondary': 20
    },
    totalStock: 120,
    mainImage: 'https://lh3.googleusercontent.com/aida-public/AB6AXuANa_b3RLeAeNWv0IxjEWH9Ha2YS-CD6o8kGGBlUFtxyrVGp_IcBL7_AJRNy4OVndirQyXLGZlhl7_KXHBDXd3sqQ2MZyB0m2hANXnDuXk3os8YRjiUFdhuZOtU1YWllpkcv-5f56uMAvcEPTG5BUSODqN6j6bIjDTowtI9toanCC_0IXTqowGVdsgSjTXVyy8a1GRRH3ADOTVoItlFNTIzyqtjz9fB5vaxopjnVxoNSzaW7bzi7CZTEtPLXsRgnsVq_U-e4JlT87c',
    photos: [
      { url: 'https://via.placeholder.com/200x200?text=Headphones+1', is_primary: false },
      { url: 'https://via.placeholder.com/200x200?text=Headphones+2', is_primary: false }
    ],
    ratings: {
      avgRating: 4.7,
      totalRatingCount: 253
    },
    salesMetrics: {
      salesQty30d: 25,
      salesRevenue30d: 30769.58,
      avgDailySalesQty30d: 11.4
    },
    sync: {
      status: 'success',
      lastSynced: '2024-01-15T10:30:00Z',
      nextSyncAt: '2024-01-15T16:30:00Z'
    },
    createdAt: '2023-06-15T08:00:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    variantCombinations: [
      {
        id: 'var-001-black',
        sku: 'SKU-WIDGET-001-BLK',
        variantId: 'v-black',
        isActive: true,
        isMainVariant: true,
        stock: 120,
        inventoryAllocation: { 'warehouse-main': 100, 'warehouse-secondary': 20 },
        tierIndex: [0],
        tierVariation: [{ color: 'Black' }],
        photo: 'https://via.placeholder.com/100x100?text=Black',
        price: { current: 89.99, original: 129.99, currency: 'USD' },
        weight: 0.35,
        dimension: { height: 20, length: 18, width: 8 },
        salesMetrics: { salesQty30d: 200, salesRevenue30d: 17998 },
        isBundle: false
      }
    ],
    merchantProduct: {
      id: 'mp-001',
      sku: 'MASTER-WATCH-SLV-EXTENDED-LONG-CODE-1234567890-THIS-IS-A-VERY-LONG-SKU-FOR-TESTING',
      name: 'Wireless Headphones Master'
    }
  },
  {
    id: 'prod-002',
    sku: 'SKU-PHONE-CASE-001',
    productListingId: 'pl-002',
    accountId: 'acc-shopee-001',
    marketplace: 'shopee',
    title: 'Premium Leather Phone Case - iPhone 15 Pro',
    description: 'Genuine leather case with card slots',
    brandName: 'CaseMaster',
    status: 'live',
    statusNote: '',
    isActive: true,
    category: [],
    price: {
      current: 29.99,
      original: 39.99,
      currency: 'USD'
    },
    inventoryAllocation: { 'warehouse-main': 500 },
    totalStock: 500,
    mainImage: 'https://via.placeholder.com/200x200?text=PhoneCase',
    photos: [{ url: 'https://via.placeholder.com/200x200?text=PhoneCase+1', is_primary: true }],
    ratings: { avgRating: 4.7, totalRatingCount: 890 },
    salesMetrics: { salesQty30d: 156, salesRevenue30d: 4678.44, avgDailySalesQty30d: 5.2 },
    sync: { status: 'success', lastSynced: '2024-01-15T09:00:00Z' },
    createdAt: '2023-09-10T12:00:00Z',
    updatedAt: '2024-01-15T09:00:00Z'
  },
  {
    id: 'prod-003',
    sku: 'SKU-LAPTOP-STAND-001',
    productListingId: 'pl-003',
    accountId: 'acc-lazada-001',
    marketplace: 'lazada',
    title: 'Adjustable Aluminum Laptop Stand',
    description: 'Ergonomic laptop stand with adjustable height',
    brandName: 'ErgoTech',
    status: 'live',
    statusNote: '',
    isActive: true,
    category: [],
    price: { current: 49.99, original: 69.99, currency: 'USD' },
    inventoryAllocation: { 'warehouse-main': 250 },
    totalStock: 250,
    mainImage: 'https://via.placeholder.com/200x200?text=LaptopStand',
    photos: [],
    ratings: { avgRating: 4.8, totalRatingCount: 2100 },
    salesMetrics: { salesQty30d: 89, salesRevenue30d: 4449.11, avgDailySalesQty30d: 2.97 },
    sync: { status: 'success', lastSynced: '2024-01-15T08:00:00Z' },
    createdAt: '2023-04-20T10:00:00Z',
    updatedAt: '2024-01-15T08:00:00Z'
  },
  {
    id: 'prod-004',
    sku: 'SKU-USB-HUB-001',
    productListingId: 'pl-004',
    accountId: 'acc-tokopedia-001',
    marketplace: 'tokopedia',
    title: 'USB-C Hub 7-in-1 Multiport Adapter',
    description: 'USB-C hub with HDMI, USB 3.0, SD card reader',
    brandName: 'TechConnect',
    status: 'sold_out',
    statusNote: 'Restock expected 2024-02-01',
    isActive: true,
    category: [],
    price: { current: 39.99, original: 49.99, currency: 'USD' },
    inventoryAllocation: {},
    totalStock: 0,
    mainImage: 'https://via.placeholder.com/200x200?text=USBHub',
    photos: [],
    ratings: { avgRating: 4.3, totalRatingCount: 560 },
    salesMetrics: { salesQty30d: 45, salesRevenue30d: 1799.55, avgDailySalesQty30d: 1.5 },
    sync: { status: 'success', lastSynced: '2024-01-15T07:00:00Z' },
    createdAt: '2023-07-15T14:00:00Z',
    updatedAt: '2024-01-15T07:00:00Z'
  },
  {
    id: 'prod-005',
    sku: 'SKU-KEYBOARD-001',
    productListingId: 'pl-005',
    accountId: 'acc-tiktok-001',
    marketplace: 'tiktok',
    title: 'Mechanical Gaming Keyboard RGB',
    description: 'Full-size mechanical keyboard with RGB backlighting',
    brandName: 'GameGear',
    status: 'live',
    statusNote: '',
    isActive: true,
    category: [],
    price: { current: 79.99, original: 99.99, currency: 'USD' },
    inventoryAllocation: { 'warehouse-main': 175, 'warehouse-secondary': 75 },
    totalStock: 250,
    mainImage: 'https://via.placeholder.com/200x200?text=Keyboard',
    photos: [],
    ratings: { avgRating: 4.6, totalRatingCount: 3200 },
    salesMetrics: { salesQty30d: 210, salesRevenue30d: 16797.90, avgDailySalesQty30d: 7 },
    sync: { status: 'success', lastSynced: '2024-01-15T11:00:00Z' },
    createdAt: '2023-03-01T09:00:00Z',
    updatedAt: '2024-01-15T11:00:00Z'
  },
  {
    id: 'prod-006',
    sku: 'SKU-MOUSE-001',
    productListingId: 'pl-006',
    accountId: 'acc-shopee-002',
    marketplace: 'shopee',
    title: 'Wireless Gaming Mouse 16000 DPI',
    description: 'High-precision wireless gaming mouse',
    brandName: 'GameGear',
    status: 'draft',
    statusNote: 'Awaiting product images',
    isActive: false,
    category: [],
    price: { current: 59.99, original: 79.99, currency: 'USD' },
    inventoryAllocation: { 'warehouse-main': 300 },
    totalStock: 300,
    mainImage: 'https://via.placeholder.com/200x200?text=Mouse',
    photos: [],
    ratings: { avgRating: 0, totalRatingCount: 0 },
    salesMetrics: { salesQty30d: 0, salesRevenue30d: 0, avgDailySalesQty30d: 0 },
    sync: { status: 'pending' },
    createdAt: '2024-01-10T16:00:00Z',
    updatedAt: '2024-01-14T10:00:00Z'
  },
  {
    id: 'prod-007',
    sku: 'SKU-CHARGER-001',
    productListingId: 'pl-007',
    accountId: 'acc-lazada-001',
    marketplace: 'lazada',
    title: 'Fast Charging USB-C Wall Charger 65W',
    description: 'GaN charger with multiple ports',
    brandName: 'PowerTech',
    status: 'live',
    statusNote: '',
    isActive: true,
    category: [],
    price: { current: 34.99, original: 44.99, currency: 'USD' },
    inventoryAllocation: { 'warehouse-main': 420 },
    totalStock: 420,
    mainImage: 'https://via.placeholder.com/200x200?text=Charger',
    photos: [],
    ratings: { avgRating: 4.4, totalRatingCount: 1800 },
    salesMetrics: { salesQty30d: 178, salesRevenue30d: 6228.22, avgDailySalesQty30d: 5.93 },
    sync: { status: 'success', lastSynced: '2024-01-15T12:00:00Z' },
    createdAt: '2023-08-22T11:00:00Z',
    updatedAt: '2024-01-15T12:00:00Z'
  },
  {
    id: 'prod-008',
    sku: 'SKU-SPEAKER-001',
    productListingId: 'pl-008',
    accountId: 'acc-tokopedia-001',
    marketplace: 'tokopedia',
    title: 'Portable Bluetooth Speaker Waterproof',
    description: 'IPX7 waterproof speaker with 24h battery',
    brandName: 'SoundMax',
    status: 'disabled',
    statusNote: 'Discontinued',
    isActive: false,
    category: [],
    price: { current: 45.99, original: 59.99, currency: 'USD' },
    inventoryAllocation: { 'warehouse-main': 15 },
    totalStock: 15,
    mainImage: 'https://via.placeholder.com/200x200?text=Speaker',
    photos: [],
    ratings: { avgRating: 4.2, totalRatingCount: 950 },
    salesMetrics: { salesQty30d: 8, salesRevenue30d: 367.92, avgDailySalesQty30d: 0.27 },
    sync: { status: 'success', lastSynced: '2024-01-10T06:00:00Z' },
    createdAt: '2022-11-01T08:00:00Z',
    updatedAt: '2024-01-10T06:00:00Z'
  }
]

/**
 * Generate mock API response for product listings
 */
export function getMockProductListingResponse(
  page: number = 1,
  pageSize: number = 20,
  storeIds?: string[]
): ProductListingApiResponse {
  let filteredProducts = mockProducts

  // Filter by store IDs if provided
  if (storeIds && storeIds.length > 0) {
    filteredProducts = mockProducts.filter(p => storeIds.includes(p.accountId))
  }

  const totalCount = filteredProducts.length
  const totalPages = Math.ceil(totalCount / pageSize)
  const startIndex = (page - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex)

  return {
    success: true,
    data: {
      productListings: paginatedProducts,
      pagination: {
        page,
        pageSize,
        totalCount,
        totalPages,
        hasNext: page < totalPages,
        hasPrevious: page > 1
      }
    }
  }
}

/**
 * Get mock stores with IDs for test mode
 */
export function getMockStores(): Store[] {
  return mockStores
}

/**
 * Get all mock store IDs
 */
export function getMockStoreIds(): string[] {
  return mockStores.map(store => store.id)
}
